import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../services/supabaseClient'
import { ensureUserAccount } from '../services/accountService'

export default function AuthVerifyHandler() {
  const navigate = useNavigate()
  const [message, setMessage] = useState('Verifying your link...')

  useEffect(() => {
    // Handle the Supabase auth verify redirect
    const handleVerification = async () => {
      // Modern Supabase uses hash fragments - check those first
      const hashParams = new URLSearchParams(window.location.hash.substring(1))
      const hasHashAuth = hashParams.get('access_token') || hashParams.get('token_hash')

      // If we have hash parameters, redirect to AuthCallback which handles them properly
      if (hasHashAuth) {
        console.log('Modern auth detected (hash fragments), redirecting to AuthCallback')
        window.location.href = `/auth/callback${window.location.hash}`
        return
      }

      // Fall back to old query parameter method for backward compatibility
      const urlParams = new URLSearchParams(window.location.search)
      const token = urlParams.get('token')
      const tokenHash = urlParams.get('token_hash')
      const type = urlParams.get('type')
      const actualToken = tokenHash || token

      console.log('Auth verify handler (legacy):', {
        token: actualToken ? 'present' : 'missing',
        type,
        method: tokenHash ? 'token_hash' : 'token'
      })

      if (type === 'recovery' && actualToken) {
        // This is a password recovery link
        // Supabase will automatically handle the token verification
        // We just need to wait for the auth state to update

        // Give Supabase a moment to process the token
        setTimeout(() => {
          // Redirect to the update password page
          navigate('/update-password')
        }, 1000)
      } else if (type === 'signup' && actualToken) {
        // Email confirmation - verify the token and log user in
        try {
          setMessage('Confirming your email...')

          // Verify the email with the token
          const { data, error } = await supabase.auth.verifyOtp({
            token_hash: actualToken,
            type: 'signup'
          })

          if (error) {
            console.error('Email verification error:', error)
            navigate('/login', {
              state: {
                message: 'Email verification failed. Please try logging in.',
                messageType: 'error'
              }
            })
            return
          }

          if (data?.session) {
            console.log('Email verified successfully, setting up account...')
            setMessage('Setting up your account...')

            // Get selected plan from sessionStorage (set when user clicked "Start Free Trial")
            const selectedPlan = sessionStorage.getItem('selectedPlan');
            console.log('Selected plan from sessionStorage:', selectedPlan);

            // Convert 'pro' to 'professional' for database consistency
            const normalizedPlan = selectedPlan === 'pro' ? 'professional' : selectedPlan;

            // Ensure user account is created with selected plan
            await ensureUserAccount(data.session, normalizedPlan)

            // Clear the stored plan
            sessionStorage.removeItem('selectedPlan');

            // Redirect to orgs page to start connecting Salesforce
            console.log('Account set up complete, redirecting to orgs page')
            navigate('/orgs', { replace: true })
          } else {
            // No session created, send to login
            navigate('/login?verified=true')
          }
        } catch (err) {
          console.error('Error during email verification:', err)
          navigate('/login', {
            state: {
              message: 'An error occurred during verification. Please try logging in.',
              messageType: 'error'
            }
          })
        }
      } else {
        // Unknown type or missing parameters
        console.error('Auth verify: Unknown type or missing parameters', {
          type,
          hasToken: !!actualToken,
          url: window.location.href
        })

        navigate('/login', {
          state: {
            message: 'Invalid or expired verification link. Please try again or contact support.',
            messageType: 'error'
          }
        })
      }
    }

    handleVerification()
  }, [navigate])
  
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">{message}</p>
      </div>
    </div>
  )
}